import type { EmblaCarouselType } from 'embla-carousel';

import { CompanyProfile, ComponentType } from 'models';
import { StringHelpers } from 'helpers/StringHelpers';

export const ProductSeriesHelpers = {
    findIndexBySlug: (productSeries: CompanyProfile['productSeries'], slug: string) => {
        return productSeries.findIndex((series) => StringHelpers.sluggify(series.seriesName || '') === slug);
    },

    scrollEmblaToIndex: (embla: EmblaCarouselType | null, index: number) => {
        if (!embla) return;
        const totalSlides = embla.slideNodes().length;
        const totalSnaps = embla.scrollSnapList().length;
        if (totalSnaps === 0) return;
        const slidesPerSnap = Math.ceil(totalSlides / totalSnaps);
        embla.scrollTo(Math.floor(index / slidesPerSnap));
    },

    scrollProductSeriesIntoView: () => {
        requestAnimationFrame(() => {
            document.getElementById('product-series')?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        });
    },

    getCompletenessScore: (series: CompanyProfile['productSeries'][0]) => {
        let score = 0;

        if (series.description) score += 10 + Math.ceil(series.description.length / 100);
        if (series.productSeriesImage) score += 20;

        if (series.distinguishingFeatures?.length) score += series.distinguishingFeatures?.length;
        if (series.productNames?.length) score += Math.ceil(series.productNames?.length / 2);
        if (series.models?.length) score += series.models?.length / 2;

        if (series.voltage?.min || series.voltage?.max) score += 5;
        if (series.power?.min || series.power?.max) score += 5;
        if (series.current?.min || series.current?.max) score += 5;
        if (series.type === 'other') score -= 30;

        return score;
    },

    filterProductSeries: (
        productSeries: CompanyProfile['productSeries'],
        selectedType: ComponentType | null,
        customSortOrder: boolean,
    ) => {
        const filtered = !selectedType ? productSeries : productSeries.filter((series) => series.type === selectedType);

        if (customSortOrder) {
            return filtered;
        }

        return [...filtered].sort((a, b) => {
            const scoreA = ProductSeriesHelpers.getCompletenessScore(a);
            const scoreB = ProductSeriesHelpers.getCompletenessScore(b);

            if (scoreB !== scoreA) {
                return scoreB - scoreA;
            }

            return (a.seriesName || '').localeCompare(b.seriesName || '');
        });
    },

    getAvailableTypes: (productSeries: CompanyProfile['productSeries']) => {
        const types: ComponentType[] = [];
        const typeCounts: Record<ComponentType, number> = {} as Record<ComponentType, number>;

        productSeries.forEach((series) => {
            if (series.type) {
                if (!types.includes(series.type)) {
                    types.push(series.type);
                    typeCounts[series.type] = 0;
                }
                typeCounts[series.type]++;
            }
        });

        types.sort((a, b) => typeCounts[b] - typeCounts[a]);

        return { types, typeCounts };
    },
};
